// Assignments («التكليفات»): the shapes of /api/econic/assignments (services: AssignmentContracts.cs; camel-case JSON, nulls left out) and the
// small pure helpers the pages, the bell and the chat cards share. A leader's request is the root of a tree; each node is owned by one person,
// assigned by the person above (who decides its packages); a package goes up bottom line first, and the one that reaches the requester is
// filed in the Knowledge Bank as a version pending final approval.

export type AssignmentStatus = "assigned" | "in-progress" | "submitted" | "returned" | "approved" | "declined" | "cancelled";
/** What the next move waits on (the chips). */
export type AssignmentStep = "assigned" | "working" | "with-team" | "submitted" | "returned" | "declined" | "approved" | "completed" | "cancelled";
export type AssignmentKind = "study" | "report" | "country-report" | "policy-paper" | "briefing" | "data" | "other";
export type AssignmentView = "action" | "mine" | "delegated" | "requested" | "watching" | "done";
/**
 * What the viewer must do on a node now: accept · revise · brief-changed (the owner); review · gate · final · acknowledge (a package at their
 * step); reassign (a decline) · extension (a date request) · answer (a question) — for whoever assigned the node, or acts for them.
 */
export type MyAction = "accept" | "revise" | "review" | "gate" | "final" | "acknowledge" | "reassign" | "extension" | "answer" | "brief-changed";
export type Priority = "normal" | "high" | "urgent";
export type Sensitivity = "public" | "internal" | "restricted" | "confidential";

/** A text in English and Arabic (types, stages, checks). */
export interface LocalText {
  en: string;
  ar?: string;
}

export interface PersonRef {
  id: string;
  name: string;
  nameAr?: string;
  title?: string;
  titleAr?: string;
}

export interface AssignmentBrief {
  objective?: string;
  questions?: string[];
  scope?: string;
  context?: string;
  requesterWords?: string;
  delegatorNote?: string;
  language?: string;
}

export interface DeliverableSpec {
  key: string;
  kind: string;
  title: string;
  titleAr?: string;
  required: boolean;
  criteria?: string[];
}

export interface PackageFinding {
  text: string;
  figure?: string;
  asOf?: string;
  source?: string;
}

export interface PackageOption {
  name: string;
  pros?: string;
  cons?: string;
}

/** A deliverable handed over: a conversation's document (artifact), an attached file, a Country Report (brief) or a Knowledge Bank document. */
export interface DeliverableRef {
  specKey: string;
  type: "artifact" | "attachment" | "brief" | "kb-document";
  id?: string;
  version?: number;
  title?: string;
  note?: string;
}

export interface PackageSource {
  title: string;
  url?: string;
  documentId?: string;
  asOf?: string;
}

export interface PointResponse {
  pointId: string;
  response: string;
  where?: string;
}

export interface PackageEndorsement {
  userId: string;
  name: string;
  title?: string;
  note: string;
  at: string;
}

/** econic.assignment.package/1 — bottom line first (completed staff work). */
export interface AssignmentPackage {
  bottomLine?: string;
  decisionRequested?: string;
  keyFindings?: PackageFinding[];
  recommendations?: string[];
  options?: PackageOption[];
  risksAndGaps?: string[];
  deliverables?: DeliverableRef[];
  sources?: PackageSource[];
  responses?: PointResponse[];
  changeSummary?: string;
  endorsements?: PackageEndorsement[];
  questionsAnswered?: Record<string, string>;
  language?: string;
  /** The type's own sections (talkingPoints, legalBasis …), as text. */
  sections?: Record<string, string>;
}

/** A point a return asks to address; `anchor` is the part of the package it is about (bottomLine, finding:2 …). */
export interface ReturnPoint {
  id: string;
  text: string;
  anchor?: string;
}

/** The Knowledge Bank's six checks (ticked by whoever sends the package to the requester). */
export interface AssignmentChecklist {
  accurate: boolean;
  relevant: boolean;
  latestVersion: boolean;
  metadataComplete: boolean;
  sensitivityCorrect: boolean;
  noPersonalData: boolean;
}

export const CHECKLIST_KEYS = ["accurate", "relevant", "latestVersion", "metadataComplete", "sensitivityCorrect", "noPersonalData"] as const;

export interface AssignmentListItem {
  id: string;
  rootId: string;
  reference: string;
  title: string;
  titleAr?: string;
  kind: AssignmentKind;
  status: AssignmentStatus;
  step: AssignmentStep;
  depth: number;
  owner: PersonRef;
  assignedBy: PersonRef;
  requester: PersonRef;
  holder?: PersonRef;
  dueAt?: string;
  /** Working days to the due date (negative once overdue). */
  workingDaysLeft?: number;
  overdue: boolean;
  priority: Priority;
  /** Names from the requester down to this node's owner. */
  path: string[];
  openChildren: number;
  children: number;
  needsMyAction: boolean;
  myAction?: MyAction;
  updatedAt: string;
  createdAt: string;
  /** The conversation the request was made in (the root, for its requester only). */
  sourceThreadId?: string | null;
  typeKey?: string;
  typeName?: LocalText;
  /** While a package waits: review | gate:<n> | final. */
  stage?: string;
  onHold?: boolean;
  /** No draft yet with little time left (the type's at-risk share). */
  atRisk?: boolean;
  /** The waiting package's bottom line, for the people who decide it (decide from the list or the bell). */
  bottomLine?: string;
  /** The owner's latest progress note (a holding reply). */
  progress?: string;
  progressAt?: string;
  /** The owner took it while this person is away (acting cover). */
  ownerActingFor?: PersonRef;
  /** The viewer's action is for this person, whom they act for. */
  actingFor?: PersonRef;
  /** The brief (here or above) changed since the owner last acknowledged it. */
  briefChanged?: boolean;
  departmentKey?: string;
  sensitivity?: Sensitivity;
  /** The node's revision: a decision taken from a list sends it, so a package that changed meanwhile is not decided blind. */
  revision?: number;
}

export interface ChainHop {
  assignmentId: string;
  depth: number;
  assignedBy: PersonRef;
  owner: PersonRef;
  status: AssignmentStatus;
  step: AssignmentStep;
  dueAt?: string;
  current: boolean;
  title: string;
  titleAr?: string | null;
}

/** The Knowledge Bank side of a package that reached the requester. */
export interface AssignmentKbState {
  state: "filing" | "pending-final" | "final" | "returned" | "not-filed";
  documentId?: string;
  versionId?: string;
  versionNumber?: number;
  note?: string;
}

export interface SubmissionView {
  id: string;
  assignmentId: string;
  number: number;
  submittedBy: PersonRef;
  submittedAt: string;
  package: AssignmentPackage;
  decision: "pending" | "approved" | "returned" | "withdrawn";
  decidedBy?: PersonRef;
  decidedAt?: string;
  decisionNote?: string;
  returnPoints: ReturnPoint[];
  forwardedFromSubmissionId?: string;
  kb?: AssignmentKbState;
  /** While it waits: the step it is at (review | gate:<n> | final). */
  stage?: string;
  /** Every decision on it, step by step (a level's review, each gate, the requester). */
  decisions?: DecisionView[];
  /** The package as sent, when an approver amended it ("approve with minor edits"). */
  original?: AssignmentPackage;
}

export interface DecisionView {
  id: string;
  stage: string;
  stageName: LocalText;
  /** approved | returned | acknowledged */
  decision: string;
  decidedBy: PersonRef;
  actingFor?: PersonRef;
  note?: string;
  points: ReturnPoint[];
  checks?: Record<string, boolean>;
  at: string;
}

export interface EventView {
  id: string;
  assignmentId: string;
  type: string;
  actor?: PersonRef;
  fromStatus?: string;
  toStatus?: string;
  note?: string;
  data?: Record<string, unknown>;
  at: string;
  /** The title of the node the step was taken on. */
  title?: string | null;
  titleAr?: string | null;
}

export interface AllowedActions {
  accept: boolean;
  decline: boolean;
  work: boolean;
  delegate: boolean;
  reassign: boolean;
  savePackage: boolean;
  submit: boolean;
  decide: boolean;
  /** Approve and send up (the delegator owns the level above). */
  forward: boolean;
  cancel: boolean;
  comment: boolean;
  /** The requester's final approval (the root's package). */
  finalDecision: boolean;
  /** The six checks go with this package (it reaches the requester). */
  checklistRequired: boolean;
  /** File the waiting package in the Knowledge Bank again (the filing failed). */
  refile?: boolean;
  /** The step of the waiting package: review | gate:<n> | final. */
  stage?: string;
  /** The final step asks for an acknowledgement, not an approval. */
  acknowledge?: boolean;
  requestExtension?: boolean;
  decideExtension?: boolean;
  hold?: boolean;
  resume?: boolean;
  editBrief?: boolean;
  /** Pass the request to another department's director (its owner, at the root). */
  redirect?: boolean;
  acknowledgeBrief?: boolean;
  /** Ask the person who assigned it a question (the clock pauses until it is answered). */
  ask?: boolean;
  answer?: boolean;
  /** Post a progress note (a holding reply). */
  progress?: boolean;
  /** Keep people informed (CC). */
  watchers?: boolean;
  merge?: boolean;
  /** Draft the package from the parts approved below. */
  consolidate?: boolean;
  /** The decision would be taken for this person (acting cover). */
  actingFor?: string;
}

export interface AssignmentBriefAbove {
  assignmentId: string;
  depth: number;
  title: string;
  owner: PersonRef;
  brief: AssignmentBrief;
}

export interface AssignmentDetail {
  node: AssignmentListItem;
  brief: AssignmentBrief;
  deliverables: DeliverableSpec[];
  chain: ChainHop[];
  above: AssignmentBriefAbove[];
  parts: AssignmentListItem[];
  submissions: SubmissionView[];
  /** The latest approved package of each part (the owner consolidates them). */
  partsApproved: SubmissionView[];
  draft?: AssignmentPackage;
  openPoints: ReturnPoint[];
  events: EventView[];
  actions: AllowedActions;
  sensitivity: Sensitivity;
  departmentKey?: string;
  sourceThreadId?: string;
  myThreadId?: string;
  kb?: AssignmentKbState;
  revision: number;
  /** The level above's open points, which "approve and send up" answers. */
  parentOpenPoints?: ReturnPoint[];
  /** The type the request started on (absent for a request with no type: every level, then the requester). */
  type?: AssignmentTypeRef;
  /** The steps on the way to the requester's decision. */
  stages?: StageView[];
  comments?: CommentView[];
  extensions?: ExtensionView[];
  /** Earlier versions of the brief, newest first (the current one is `brief`). */
  briefVersions?: BriefVersionView[];
  watchers?: WatcherView[];
  /** The owner's readiness check of their draft. */
  readiness?: ReadinessItem[];
  onHoldSince?: string;
  holdReason?: string;
  /** The office that raised the request on the requester's behalf. */
  createdBy?: PersonRef;
  briefRevision?: number;
}

export interface ReportView {
  person: PersonRef;
  departmentKey?: string;
  openAssignments: number;
  /** override | department | unit | leadership | director */
  basis: string;
  /** Away: whoever acts for them receives new work. */
  actingFor?: PersonRef;
}

// ------------------------------------------------------------------------------------------------------------- v2: types, steps, people

export interface TypeDue {
  normal?: number;
  high?: number;
  urgent?: number;
}

export interface TypeSection {
  key: string;
  title: LocalText;
  required: boolean;
}

export interface TypeCheck {
  key: string;
  text: LocalText;
}

/** One step of a type's journey (ask | work | review | gate | final | file). */
export interface JourneyStep {
  kind: "ask" | "work" | "review" | "gate" | "final" | "file";
  en: string;
  ar: string;
  when?: string;
  enabled: boolean;
  note?: string;
}

export interface AssignmentTypeRef {
  key: string;
  version: number;
  name: LocalText;
  /** chain | direct */
  review: string;
  /** requester | acknowledge */
  final: string;
  /** at-requester | never */
  kbFile: string;
  extensionsAllowed: boolean;
  extensionsMax: number;
  sections: TypeSection[];
  require: string[];
  bottomLineWords: number;
}

/** A type someone may pick for a new request. */
export interface AssignmentTypeView {
  key: string;
  version: number;
  name: LocalText;
  description?: LocalText;
  kind: AssignmentKind;
  canRequest: boolean;
  due: TypeDue;
  sensitivity?: Sensitivity;
  deliverables: DeliverableSpec[];
  sections: TypeSection[];
  require: string[];
  bottomLineWords: number;
  review: string;
  final: string;
  kbFile: string;
  teamView: boolean;
  extensionsAllowed: boolean;
  redirect: boolean;
  journey: JourneyStep[];
}

/** A step on the way to the requester: done | current | next | skipped (on, nobody to review) | off (switched off, or its condition does not hold). */
export interface StageView {
  key: string;
  kind: "review" | "gate" | "final";
  name: LocalText;
  deciders: PersonRef[];
  checklist: TypeCheck[];
  state: "done" | "current" | "next" | "skipped" | "off";
  when?: string;
}

export interface CommentView {
  id: string;
  assignmentId: string;
  submissionNumber?: number;
  anchor?: string;
  kind: "comment" | "question" | "answer" | "progress";
  replyToId?: string;
  text: string;
  author: PersonRef;
  actingFor?: PersonRef;
  at: string;
  resolvedAt?: string;
}

export interface ExtensionView {
  id: string;
  /** extension | proposed (on accepting) */
  kind: string;
  requestedBy: PersonRef;
  requestedAt: string;
  currentDueAt?: string;
  proposedDueAt: string;
  reason: string;
  status: "pending" | "approved" | "declined" | "withdrawn";
  decidedBy?: PersonRef;
  decidedAt?: string;
  note?: string;
}

export interface BriefVersionView {
  number: number;
  title: string;
  titleAr?: string;
  brief: AssignmentBrief;
  deliverables: DeliverableSpec[];
  dueAt?: string;
  priority: Priority;
  /** Who made the change that ended this version, and when. */
  changedBy?: PersonRef;
  changedAt: string;
  changeNote?: string;
}

export interface WatcherView {
  person: PersonRef;
  /** cc | office */
  role: string;
}

export interface ReadinessItem {
  key: string;
  ok: boolean;
  severity: "block" | "warn";
  en: string;
  ar: string;
}

export type PortfolioHealth = "waiting-on-me" | "late" | "at-risk" | "on-hold" | "on-track" | "done";

export interface PortfolioItem {
  item: AssignmentListItem;
  health: PortfolioHealth;
  departmentName?: string;
  departmentNameAr?: string;
}

export interface PortfolioGroup {
  key: string;
  name: LocalText;
  open: number;
  late: number;
  atRisk: number;
}

export interface PortfolioView {
  items: PortfolioItem[];
  counts: Record<PortfolioHealth, number>;
  byDepartment: PortfolioGroup[];
  byType: PortfolioGroup[];
}

export interface MorningSummary {
  waitingOnMe: number;
  late: number;
  atRisk: number;
  onTrack: number;
  updates: number;
  top: AssignmentListItem[];
  en: string;
  ar: string;
}

/** Acting cover (someone away, someone acting for them, until a date) or an office line (who raises requests for a principal). */
export interface CoverView {
  id: string;
  kind: "acting" | "office";
  person: PersonRef;
  acting: PersonRef;
  startsAt: string;
  endsAt?: string;
  note?: string;
  active: boolean;
}

export interface CoverInput {
  actingUserId: string;
  kind?: "acting" | "office";
  userId?: string;
  startsAt?: string;
  endsAt?: string;
  note?: string;
}

export interface BatchDecideResult {
  id: string;
  ok: boolean;
  error?: string;
  errorAr?: string;
}

// the type editor (console)

export interface TypeProblem {
  path: string;
  en: string;
  ar: string;
}

export interface TypeVersionView {
  id: string;
  version: number;
  /** draft | in-review | published | retired | rejected */
  status: string;
  /** seed | console */
  source: string;
  definition: Record<string, unknown>;
  changeNote?: string;
  proposedBy?: PersonRef;
  proposedAt?: string;
  decidedBy?: PersonRef;
  decidedAt?: string;
  decisionNote?: string;
  journey: JourneyStep[];
  problems: TypeProblem[];
}

export interface TypeAdminView {
  key: string;
  name: LocalText;
  published?: TypeVersionView;
  draft?: TypeVersionView;
  retired: boolean;
  history: TypeVersionView[];
}

export interface TypeValidation {
  ok: boolean;
  problems: TypeProblem[];
  journey: JourneyStep[];
}

export interface RoutingSuggestion {
  departmentKey?: string;
  departmentName?: string;
  departmentNameAr?: string;
  owners: PersonRef[];
  reason: string;
}

// ------------------------------------------------------------------------------------------------------------- inputs

export interface CreateAssignmentInput {
  title: string;
  ownerUserId: string;
  kind?: AssignmentKind;
  titleAr?: string;
  brief?: AssignmentBrief;
  deliverables?: DeliverableSpec[];
  dueAt?: string;
  priority?: Priority;
  sensitivity?: Sensitivity;
  departmentKey?: string;
  sourceThreadId?: string;
  /** A type of request (its key): it sets the defaults and the steps. */
  typeKey?: string;
  /** For an executive office: the principal the request is raised for (who decides it). */
  onBehalfOf?: string;
  /** People kept informed (CC). */
  cc?: string[];
}

export interface DelegateChildInput {
  ownerUserId: string;
  title?: string;
  note?: string;
  dueAt?: string;
  deliverables?: DeliverableSpec[];
  questions?: string[];
}

export interface DecideInput {
  decision: "approve" | "forward" | "return" | "acknowledge";
  note?: string;
  points?: string[];
  checklist?: AssignmentChecklist;
  responses?: PointResponse[];
  revision?: number;
  /** A gate's checklist, each key ticked. */
  checks?: Record<string, boolean>;
  /** "Approve with minor edits": only what is given changes; the package as sent is kept. */
  amend?: { bottomLine?: string; decisionRequested?: string; recommendations?: string[] };
}

export interface BriefEditInput {
  title?: string;
  titleAr?: string;
  brief?: AssignmentBrief;
  deliverables?: DeliverableSpec[];
  dueAt?: string;
  priority?: Priority;
  changeNote?: string;
  revision?: number;
}

export interface CommentInput {
  text: string;
  /** comment (default) | question | answer | progress */
  kind?: "comment" | "question" | "answer" | "progress";
  /** bottomLine | decisionRequested | finding:<n> | recommendation:<n> | option:<n> | risk:<n> | source:<n> | deliverable:<key> | section:<key> */
  anchor?: string;
  submissionNumber?: number;
  replyToId?: string;
}

// ------------------------------------------------------------------------------------------------------------- helpers

/** A text in the reader's language. */
export const localText = (t: LocalText | undefined | null, lang: "en" | "ar"): string => (t ? (lang === "ar" && t.ar) || t.en : "");

/** The kind of a package's step: review (a level), gate (a gate's review), final (the requester). */
export const stageKind = (stage: string | undefined | null): "review" | "gate" | "final" | null =>
  !stage ? null : stage.startsWith("gate:") ? "gate" : stage === "final" ? "final" : "review";

/** The parts of a package an anchored comment may point at, in reading order (finding:1 is the first finding). */
export function packageAnchors(p: AssignmentPackage | null | undefined): string[] {
  if (!p) return [];
  const n = (prefix: string, list: unknown[] | undefined) => (list ?? []).map((_, i) => `${prefix}:${i + 1}`);
  return [
    ...(p.bottomLine ? ["bottomLine"] : []),
    ...(p.decisionRequested ? ["decisionRequested"] : []),
    ...n("finding", p.keyFindings),
    ...n("recommendation", p.recommendations),
    ...n("option", p.options),
    ...n("risk", p.risksAndGaps),
    ...Object.keys(p.sections ?? {}).map((k) => `section:${k}`),
    ...(p.deliverables ?? []).map((d) => `deliverable:${d.specKey}`),
    ...n("source", p.sources),
  ];
}

/** The text an anchor points at in a package (for the comment's quote), or "". */
export function anchorText(p: AssignmentPackage | null | undefined, anchor: string): string {
  if (!p) return "";
  const [kind, raw] = anchor.split(":");
  const i = Number(raw) - 1;
  switch (kind) {
    case "bottomLine": return p.bottomLine ?? "";
    case "decisionRequested": return p.decisionRequested ?? "";
    case "finding": return p.keyFindings?.[i]?.text ?? "";
    case "recommendation": return p.recommendations?.[i] ?? "";
    case "option": return p.options?.[i]?.name ?? "";
    case "risk": return p.risksAndGaps?.[i] ?? "";
    case "source": return p.sources?.[i]?.title ?? "";
    case "section": return p.sections?.[raw ?? ""] ?? "";
    case "deliverable": return p.deliverables?.find((d) => d.specKey === raw)?.title ?? raw ?? "";
    default: return "";
  }
}

/** What changed between two versions of a brief: title · objective · questions (added / removed) · scope · context · note · deliverables · due · priority. */
export function briefChanges(
  before: { title: string; titleAr?: string; brief: AssignmentBrief; deliverables: DeliverableSpec[]; dueAt?: string; priority: Priority },
  after: { title: string; titleAr?: string; brief: AssignmentBrief; deliverables: DeliverableSpec[]; dueAt?: string; priority: Priority },
): { field: string; added?: string[]; removed?: string[] }[] {
  const out: { field: string; added?: string[]; removed?: string[] }[] = [];
  const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  if (before.title !== after.title || (before.titleAr ?? "") !== (after.titleAr ?? "")) out.push({ field: "title" });
  for (const f of ["objective", "scope", "context", "delegatorNote"] as const)
    if ((before.brief[f] ?? "") !== (after.brief[f] ?? "")) out.push({ field: f === "delegatorNote" ? "note" : f });
  const qa = before.brief.questions ?? [];
  const qb = after.brief.questions ?? [];
  if (!same(qa, qb)) out.push({ field: "questions", added: qb.filter((q) => !qa.includes(q)), removed: qa.filter((q) => !qb.includes(q)) });
  if (!same(before.deliverables.map((d) => [d.key, d.title, d.required]), after.deliverables.map((d) => [d.key, d.title, d.required]))) out.push({ field: "deliverables" });
  if ((before.dueAt ?? "") !== (after.dueAt ?? "") && new Date(before.dueAt ?? 0).getTime() !== new Date(after.dueAt ?? 0).getTime()) out.push({ field: "due" });
  if (before.priority !== after.priority) out.push({ field: "priority" });
  return out;
}

/** What changed between two packages (the diff view): each part that differs, with the text before and after. */
export function packageChanges(before: AssignmentPackage, after: AssignmentPackage): { part: string; before: string; after: string }[] {
  const text = {
    bottomLine: (p: AssignmentPackage) => p.bottomLine ?? "",
    decisionRequested: (p: AssignmentPackage) => p.decisionRequested ?? "",
    keyFindings: (p: AssignmentPackage) => (p.keyFindings ?? []).map((f) => [f.text, f.figure, f.asOf, f.source].filter(Boolean).join(" · ")).join("\n"),
    recommendations: (p: AssignmentPackage) => (p.recommendations ?? []).join("\n"),
    options: (p: AssignmentPackage) => (p.options ?? []).map((o) => o.name).join("\n"),
    risksAndGaps: (p: AssignmentPackage) => (p.risksAndGaps ?? []).join("\n"),
    deliverables: (p: AssignmentPackage) => (p.deliverables ?? []).map((d) => `${d.specKey}: ${d.title ?? d.id ?? d.note ?? ""}`).join("\n"),
    sources: (p: AssignmentPackage) => (p.sources ?? []).map((x) => x.title).join("\n"),
  } as const;
  const out: { part: string; before: string; after: string }[] = [];
  for (const [part, read] of Object.entries(text)) {
    const a = read(before);
    const b = read(after);
    if (a !== b) out.push({ part, before: a, after: b });
  }
  for (const key of new Set([...Object.keys(before.sections ?? {}), ...Object.keys(after.sections ?? {})])) {
    const a = before.sections?.[key] ?? "";
    const b = after.sections?.[key] ?? "";
    if (a !== b) out.push({ part: `section:${key}`, before: a, after: b });
  }
  return out;
}

/** A person's name in the reader's language. */
export const personName = (p: PersonRef | undefined | null, lang: "en" | "ar"): string => (p ? (lang === "ar" && p.nameAr) || p.name : "");

export const assignmentTitle = (a: { title: string; titleAr?: string }, lang: "en" | "ar"): string => (lang === "ar" && a.titleAr) || a.title;

/** The chip tone of a step (tokens only: the looks colour them). */
export function stepTone(step: AssignmentStep, overdue = false): "neutral" | "info" | "warn" | "good" | "crit" {
  if (overdue && step !== "completed" && step !== "approved" && step !== "cancelled") return "crit";
  switch (step) {
    case "submitted":
      return "info";
    case "returned":
    case "declined":
      return "warn";
    case "approved":
    case "completed":
      return "good";
    default:
      return "neutral";
  }
}

export const isOpen = (s: AssignmentStatus): boolean => s !== "approved" && s !== "cancelled";

export const checklistComplete = (c: Partial<AssignmentChecklist> | null | undefined): c is AssignmentChecklist =>
  !!c && CHECKLIST_KEYS.every((k) => c[k] === true);

/** Words in a bottom line (the package asks for about sixty). */
export const words = (text: string | undefined): number => (text?.trim() ? text.trim().split(/\s+/).length : 0);

// ------------------------------------------------------------------------------------------------------------- page helpers (client only)

/** The bottom line's guide (AssignmentRules.BottomLineWords); the server refuses one past twice that. */
export const BOTTOM_LINE_WORDS = 60;

const ASSIGNMENT_IN_SURFACE = /:assignment:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

/** The assignment a conversation is bound to (`econic:<space>:assignment:<id>`), or null. */
export function assignmentOfSurface(surfaceKey: string | null | undefined): string | null {
  const m = ASSIGNMENT_IN_SURFACE.exec(surfaceKey ?? "");
  return m ? m[1]!.toLowerCase() : null;
}

/** The chip's label key: a package at a gate waits on its review, one at the final step on the requester, any other on a level's review. */
export function stepKey(step: AssignmentStep, depth: number, stage?: string | null): string {
  if (step !== "submitted") return step;
  const kind = stageKind(stage);
  if (kind === "gate") return "submittedGate";
  return kind === "final" || (!kind && depth === 0) ? "submittedFinal" : step;
}

/** The package waiting for a decision on a node (the newest pending one), if any. */
export function pendingSubmission(d: Pick<AssignmentDetail, "submissions"> | null | undefined): SubmissionView | null {
  const pending = (d?.submissions ?? []).filter((s) => s.decision === "pending");
  return pending.length ? pending.reduce((a, b) => (b.number > a.number ? b : a)) : null;
}

/** The newest decided package of a node, if any (what a card shows once decided). */
export function lastDecided(d: Pick<AssignmentDetail, "submissions"> | null | undefined): SubmissionView | null {
  const decided = (d?.submissions ?? []).filter((s) => s.decision === "approved" || s.decision === "returned");
  return decided.length ? decided.reduce((a, b) => (b.number > a.number ? b : a)) : null;
}

/** The hop above this node on its chain (whose owner assigned it), or null at the root. */
export function parentHop(d: Pick<AssignmentDetail, "chain" | "node">): ChainHop | null {
  return d.chain.find((h) => h.depth === d.node.depth - 1) ?? null;
}

/** Lines of a text box (one point, question or recommendation per line), trimmed, empty ones dropped. */
export const lines = (text: string | undefined | null): string[] => (text ?? "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean);

/** A deliverable reference counts once it names a file (an id) or says how it was delivered (a note), as on the server. */
export const deliverableGiven = (r: DeliverableRef | undefined): boolean => !!r && (!!r.id || !!r.note?.trim());

/**
 * Why a package may not go up yet, as on the server (AssignmentRules.SubmitProblems): open parts, a missing or overlong bottom line, a
 * required deliverable not given, a point of the last return not answered. Codes: `parts`, `bottomLine`, `bottomLineLong`,
 * `deliverable:<key>`, `points`. Empty when it may go.
 */
export function packageProblems(
  d: Pick<AssignmentDetail, "parts" | "deliverables" | "openPoints">,
  p: AssignmentPackage | null | undefined,
  /** The type's bottom-line guide (the server refuses one past twice it). */
  bottomLineWords: number = BOTTOM_LINE_WORDS,
): string[] {
  const problems: string[] = [];
  if (d.parts.some((c) => isOpen(c.status))) problems.push("parts");
  if (!p) return [...problems, "bottomLine"];
  const n = words(p.bottomLine);
  if (n === 0) problems.push("bottomLine");
  else if (n > bottomLineWords * 2) problems.push("bottomLineLong");
  const given = new Set((p.deliverables ?? []).filter(deliverableGiven).map((r) => r.specKey));
  for (const spec of d.deliverables) if (spec.required && !given.has(spec.key)) problems.push(`deliverable:${spec.key}`);
  const answered = new Set((p.responses ?? []).filter((r) => r.response?.trim()).map((r) => r.pointId));
  if (d.openPoints.some((pt) => !answered.has(pt.id))) problems.push("points");
  return problems;
}

/** A package with its empty parts left out (what is saved and sent). */
export function cleanPackage(p: AssignmentPackage): AssignmentPackage {
  const s = (v: string | undefined) => (v?.trim() ? v.trim() : undefined);
  const list = <T,>(v: T[] | undefined): T[] | undefined => (v && v.length ? v : undefined);
  return {
    ...p,
    bottomLine: s(p.bottomLine),
    decisionRequested: s(p.decisionRequested),
    keyFindings: list((p.keyFindings ?? []).filter((f) => f.text?.trim()).map((f) => ({ text: f.text.trim(), figure: s(f.figure), asOf: s(f.asOf), source: s(f.source) }))),
    recommendations: list((p.recommendations ?? []).map((r) => r.trim()).filter(Boolean)),
    options: list((p.options ?? []).filter((o) => o.name?.trim()).map((o) => ({ name: o.name.trim(), pros: s(o.pros), cons: s(o.cons) }))),
    risksAndGaps: list((p.risksAndGaps ?? []).map((r) => r.trim()).filter(Boolean)),
    deliverables: list((p.deliverables ?? []).filter(deliverableGiven)),
    sources: list((p.sources ?? []).filter((x) => x.title?.trim()).map((x) => ({ ...x, title: x.title.trim(), url: s(x.url), asOf: s(x.asOf) }))),
    responses: list((p.responses ?? []).filter((r) => r.response?.trim()).map((r) => ({ ...r, response: r.response.trim(), where: s(r.where) }))),
    changeSummary: s(p.changeSummary),
    sections: textMap(p.sections),
    questionsAnswered: textMap(p.questionsAnswered),
  };
}

/** A map of texts with its empty entries left out (a type's sections, where each question is answered); undefined when nothing is left. */
function textMap(r: Record<string, string> | undefined): Record<string, string> | undefined {
  const kept = Object.entries(r ?? {}).map(([k, v]) => [k, (v ?? "").trim()] as const).filter(([k, v]) => !!k.trim() && !!v);
  return kept.length ? Object.fromEntries(kept) : undefined;
}

/** `yyyy-mm-dd` of an instant in the reader's time zone (a date field's value), or "". */
export function dateValue(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** The end of a picked day in the reader's time zone, as an instant (a due date is the whole day). */
export function endOfDay(value: string): string | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return undefined;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 0).toISOString();
}

/** The list page's verdict: how many wait on the person, and how many of their open ones are overdue (each node once). */
export function verdictCounts(action: AssignmentListItem[], ...others: AssignmentListItem[][]): { action: number; overdue: number } {
  const seen = new Map<string, AssignmentListItem>();
  for (const a of [action, ...others].flat()) seen.set(a.id, a);
  return { action: action.length, overdue: [...seen.values()].filter((a) => a.overdue && isOpen(a.status)).length };
}

/** A root the person requested with this title (a brief card already sent, read again after a reload). */
export function sentRoot(requested: AssignmentListItem[] | undefined, title: string, sourceThreadId?: string | null): AssignmentListItem | null {
  const t = title.trim().toLowerCase();
  if (!t) return null;
  const same = (a: AssignmentListItem) => a.depth === 0 && a.title.trim().toLowerCase() === t;
  // the request sent from this conversation, when the server says where each was made; else by title alone
  const thread = sourceThreadId?.toLowerCase();
  return (requested ?? []).find((a) => same(a) && (!thread || !a.sourceThreadId || a.sourceThreadId.toLowerCase() === thread)) ?? null;
}

/** A split card is already done when every person it names holds a part of the node (cancelled parts aside). */
export function splitDone(parts: AssignmentListItem[] | undefined, owners: string[]): boolean {
  if (!owners.length || !parts?.length) return false;
  const held = new Set(parts.filter((p) => p.status !== "cancelled").map((p) => p.owner.id.toLowerCase()));
  return owners.every((o) => held.has(o.toLowerCase()));
}

/** Names of the parts a delegation event created (its journal data), for the timeline. */
export function delegatedNames(data: Record<string, unknown> | undefined, lang: "en" | "ar" = "en"): string[] {
  const parts = Array.isArray(data?.parts) ? (data!.parts as Array<Record<string, unknown>>) : [];
  const text = (v: unknown) => (typeof v === "string" ? v : "");
  return parts.map((p) => (lang === "ar" && text(p.ownerNameAr)) || text(p.ownerName)).filter(Boolean);
}

/** A number from an event's journal data (a package number, a Knowledge Bank version), if present. */
export function dataNumber(data: Record<string, unknown> | undefined, key: string): number | null {
  const v = data?.[key];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

// ------------------------------------------------------------------------------------------------------------- v2 page helpers (client only)

/** The assignment page's tabs. */
export type PageTab = "brief" | "work" | "packages" | "timeline";
export const PAGE_TABS: readonly PageTab[] = ["brief", "work", "packages", "timeline"];

/**
 * The tab a page opens on when the reader has not picked one: Packages while a decision waits on them; the Brief when it is theirs to
 * accept or a changed brief waits on them; Packages once a package is with the level above or decided (and on a return, to read it);
 * Work while parts are out; else the Brief.
 */
export function defaultTab(d: Pick<AssignmentDetail, "actions" | "node" | "parts" | "submissions">): PageTab {
  if (d.actions.decide) return "packages";
  if (d.actions.accept || d.actions.acknowledgeBrief) return "brief";
  const s = d.node.status;
  if ((s === "submitted" || s === "approved" || s === "returned") && d.submissions.length) return "packages";
  if (d.parts.length) return "work";
  return "brief";
}

/** The question waiting for an answer on a node (the newest open one), if any. */
export function openQuestion(comments: CommentView[] | undefined | null): CommentView | null {
  const open = (comments ?? []).filter((c) => c.kind === "question" && !c.resolvedAt);
  return open.length ? open.reduce((a, b) => (b.at > a.at ? b : a)) : null;
}

/** The date request waiting for a decision (an extension, or another date proposed on accepting), if any. */
export const pendingExtension = (extensions: ExtensionView[] | undefined | null): ExtensionView | null =>
  (extensions ?? []).find((e) => e.status === "pending") ?? null;

/** Extensions already agreed (the type's limit counts these; a date proposed on accepting does not). */
export const extensionsUsed = (extensions: ExtensionView[] | undefined | null): number =>
  (extensions ?? []).filter((e) => e.status === "approved" && e.kind === "extension").length;

/** The anchored comments on one package, by the part they anchor (a comment, not a question or a note). */
export function commentsByAnchor(comments: CommentView[] | undefined | null, submissionNumber: number): Map<string, CommentView[]> {
  const m = new Map<string, CommentView[]>();
  for (const c of comments ?? []) {
    if (c.kind !== "comment" || !c.anchor || c.submissionNumber !== submissionNumber) continue;
    m.set(c.anchor, [...(m.get(c.anchor) ?? []), c]);
  }
  for (const list of m.values()) list.sort((a, b) => a.at.localeCompare(b.at));
  return m;
}

/**
 * The open anchored comments a return takes as its points (as on the server's return): on the package waiting, not resolved, written by
 * the person deciding or by one of the step's deciders.
 */
export function returnComments(comments: CommentView[] | undefined | null, submissionNumber: number, authorIds: string[]): CommentView[] {
  const ids = new Set(authorIds.map((x) => x.toLowerCase()));
  return (comments ?? [])
    .filter((c) => c.kind === "comment" && !!c.anchor && !c.resolvedAt && c.submissionNumber === submissionNumber && ids.has(c.author.id.toLowerCase()))
    .sort((a, b) => a.at.localeCompare(b.at));
}

/** An anchor read back: bottomLine · decisionRequested · finding:2 (index 2) · section:talkingPoints (key) · deliverable:main (key) … */
export function parseAnchor(anchor: string | undefined | null): { kind: string; index?: number; key?: string } | null {
  if (!anchor) return null;
  const at = anchor.indexOf(":");
  if (at < 0) return { kind: anchor };
  const kind = anchor.slice(0, at);
  const rest = anchor.slice(at + 1);
  return /^\d+$/.test(rest) ? { kind, index: Number(rest) } : { kind, key: rest };
}

/** A gate's condition read back ("sensitivity >= restricted and priority = urgent"), clause by clause; null when it cannot be read. */
export function parseCondition(when: string | undefined | null): { join: "and" | "or"; field: string; op: string; value: string }[] | null {
  if (!when?.trim()) return null;
  const parts = when.trim().split(/\s+(and|or)\s+/i);
  const out: { join: "and" | "or"; field: string; op: string; value: string }[] = [];
  let join: "and" | "or" = "and";
  for (let i = 0; i < parts.length; i++) {
    if (i % 2 === 1) { join = parts[i]!.toLowerCase() === "or" ? "or" : "and"; continue; }
    const m = /^(sensitivity|priority|kind)\s*(>=|<=|==|!=|=|>|<)\s*([a-z0-9-]+)$/i.exec(parts[i]!.trim());
    if (!m) return null;
    out.push({ join, field: m[1]!.toLowerCase(), op: m[2] === "==" ? "=" : m[2]!, value: m[3]!.toLowerCase() });
  }
  return out.length ? out : null;
}

/** Where a brief question is said to be answered: by its text, or by its place (q2, 2), as the readiness check reads it. */
export function answeredWhere(map: Record<string, string> | undefined | null, question: string, index: number): string {
  return map?.[question] ?? map?.[`q${index + 1}`] ?? map?.[String(index + 1)] ?? "";
}

/** The brief as it stands (to compare with the version before it). */
export const currentBrief = (d: Pick<AssignmentDetail, "node" | "brief" | "deliverables">) => ({
  title: d.node.title, titleAr: d.node.titleAr, brief: d.brief, deliverables: d.deliverables, dueAt: d.node.dueAt, priority: d.node.priority,
});

/** The step a waiting package is at, from the stepper's steps (by the actions' stage, else the current one). */
export function currentStage(d: Pick<AssignmentDetail, "stages" | "actions">): StageView | null {
  const list = d.stages ?? [];
  return (d.actions.stage ? list.find((s) => s.key === d.actions.stage) : undefined) ?? list.find((s) => s.state === "current") ?? null;
}

/** The person the viewer decides for under acting cover (the node says it, else a decider of the step or a person on the chain). */
export function actingForPerson(d: Pick<AssignmentDetail, "actions" | "node" | "stages" | "chain">): PersonRef | null {
  const id = d.actions.actingFor?.toLowerCase();
  if (!id) return null;
  if (d.node.actingFor && d.node.actingFor.id.toLowerCase() === id) return d.node.actingFor;
  const people = [...(d.stages ?? []).flatMap((s) => s.deciders), d.node.assignedBy, d.node.requester, ...d.chain.flatMap((h) => [h.assignedBy, h.owner])];
  return people.find((p) => p.id.toLowerCase() === id) ?? null;
}

/** People to choose from, each once (the recipients and the direct reports overlap), in the order first seen. */
export function uniquePeople<T extends { person: PersonRef }>(...lists: (T[] | undefined | null)[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const r of lists.flatMap((l) => l ?? [])) {
    const id = r.person.id.toLowerCase();
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(r);
  }
  return out;
}

/** The default due of a type for a priority, in working days (the server applies it when no date is given). */
export const typeDueDays = (t: Pick<AssignmentTypeView, "due"> | null | undefined, priority: Priority): number | null => {
  const n = t?.due?.[priority] ?? t?.due?.normal;
  return typeof n === "number" && n > 0 ? n : null;
};

/** Each point a return asked for, with the answer the next package gave it (the diff view). */
export function pointAnswers(returned: Pick<SubmissionView, "returnPoints"> | null | undefined, next: Pick<SubmissionView, "package"> | null | undefined) {
  const responses = new Map((next?.package.responses ?? []).map((r) => [r.pointId, r]));
  return (returned?.returnPoints ?? []).map((p) => ({ point: p, response: responses.get(p.id)?.response ?? "", where: responses.get(p.id)?.where ?? "" }));
}

/** "Approve with minor edits": only what the approver changed (the bottom line, the decision asked, the recommendations), or undefined. */
export function amendmentOf(p: AssignmentPackage, edits: { bottomLine: string; decisionRequested: string; recommendations: string }): DecideInput["amend"] {
  const out: NonNullable<DecideInput["amend"]> = {};
  if (edits.bottomLine.trim() !== (p.bottomLine ?? "").trim()) out.bottomLine = edits.bottomLine.trim();
  if (edits.decisionRequested.trim() !== (p.decisionRequested ?? "").trim()) out.decisionRequested = edits.decisionRequested.trim();
  const recs = lines(edits.recommendations);
  if (JSON.stringify(recs) !== JSON.stringify(lines((p.recommendations ?? []).join("\n")))) out.recommendations = recs;
  return Object.keys(out).length ? out : undefined;
}

/** One line of the timeline: a journal entry, or a comment, question, answer or progress note on this node (read with its anchor). */
export type TimelineItem = { at: string; event: EventView; comment?: undefined } | { at: string; comment: CommentView; event?: undefined };

/**
 * The timeline: the journal of every node the reader sees, and this node's comments, questions, answers and progress notes in their own
 * right (their journal lines left out, so each is told once), oldest first.
 */
export function timelineItems(events: EventView[], comments: CommentView[] | undefined | null, nodeId: string): TimelineItem[] {
  const told = new Set(["commented", "asked", "answered", "progress"]);
  const own = comments ?? [];
  const items: TimelineItem[] = [
    ...events.filter((e) => !(own.length && e.assignmentId === nodeId && told.has(e.type))).map((e) => ({ at: e.at, event: e })),
    ...own.map((c) => ({ at: c.at, comment: c })),
  ];
  return items.sort((a, b) => a.at.localeCompare(b.at));
}
